import { FileData } from '@applyboard/school-applications-types-lib'
import { Ref } from 'react'
import { RawApplicationResponse } from '../../../hooks'
import { useGetTimelineItems } from '../../../hooks/useGetTimelineItems'
import { GenericError } from '../../../utils'
import { TimelineTabs } from '../../../utils/enums'
import { StudentApplication } from '../types'
import { AdditionalDocumentsTab } from './AdditionalDocumentsTab'
import { ApplicationForm } from './ApplicationForm'
import { ContactInformationTab } from './ContactInformationTab'
import { EducationHistoryTab } from './EducationHistoryTab'
import { LanguageProficiencyTab } from './LanguageProficiencyTab'
import { LegacyLanguageProficiencyTab } from './LegacyLanguageProficiencyTab'
import { hasLegacyLanguageProficiencySchema } from './utils'
import { PersonalInformationTab } from './PersonalInformationTab'
import styled from '@emotion/styled'

type ApplicationFormsProps = {
  disabled?: boolean
  selectedTab: number
  application: StudentApplication
  onSuccess: (response?: RawApplicationResponse) => void
  onError?: (error: Error) => void
  submitApplication?: () => void
  currentIntakeAvailable?: boolean
  onIntakeUnavailable?: () => void
  onCreatedApplication?: (applicationId: string) => void
  isCreatingApplication?: boolean
  focusRef?: Ref<HTMLButtonElement> | null
  onPreviousTabButton?: () => void
  onNextTabButton?: () => void
}

export function ApplicationForms(props: ApplicationFormsProps) {
  const onError = (err: GenericError) => {
    if (err instanceof Error) {
      props.onError?.(err)
    }
  }

  return (
    <ApplicationForm 
      fileData={props.application?.attributes?.files as FileData}
      documentProcessingFeatureEnabled={!!props.onCreatedApplication}>
      <Wrapper>
        <FormTab
          application={props.application}
          disabled={props.disabled}
          onError={onError}
          onSuccess={props.onSuccess}
          selectedTab={props.selectedTab}
          submitApplication={props.submitApplication}
          currentIntakeAvailable={props.currentIntakeAvailable}
          onIntakeUnavailable={props.onIntakeUnavailable}
          onCreatedApplication={props.onCreatedApplication}
          isCreatingApplication={props.isCreatingApplication}
          focusRef={props.focusRef}
          onPreviousTabButton={props.onPreviousTabButton}
          onNextTabButton={props.onNextTabButton}
        />
      </Wrapper>
    </ApplicationForm>
  )
}

function FormTab(
  props: Omit<ApplicationFormsProps, 'onError'> & {
    onError: (err: Error) => void
  },
) {
  const { timelineItems } = useGetTimelineItems(props.application)
  const timelineItem = timelineItems.find(item => item.key === props.selectedTab)

  const { applicationHasLegacyLanguageProficiency } = hasLegacyLanguageProficiencySchema(props.application);

  switch (timelineItem?.key) {
    case TimelineTabs.OTHER_REQUIRED_DOCUMENTS:
      return (
        <AdditionalDocumentsTab
          application={props.application}
          onSuccess={props.onSuccess}
          onError={props.onError}
          submitApplication={props.submitApplication}
          disabled={props.disabled}
          focusRef={props.focusRef}
          currentIntakeAvailable={props.currentIntakeAvailable}
          onIntakeUnavailable={props.onIntakeUnavailable}
          onPreviousTabButton={props.onPreviousTabButton}
        />
      )
    case TimelineTabs.LANGUAGE_PROFICIENCY:
      return applicationHasLegacyLanguageProficiency ? (
        <LegacyLanguageProficiencyTab
          onSuccess={props.onSuccess}
          application={props.application}
          onError={props.onError}
          disabled={props.disabled}
          currentIntakeAvailable={props.currentIntakeAvailable}
          onIntakeUnavailable={props.onIntakeUnavailable}
          formId={crypto.randomUUID()}
          onPreviousTabButton={props.onPreviousTabButton}
          onNextTabButton={props.onNextTabButton}
        /> 
      ) : (
        <LanguageProficiencyTab
          onSuccess={props.onSuccess}
          application={props.application}
          onError={props.onError}
          disabled={props.disabled}
          currentIntakeAvailable={props.currentIntakeAvailable}
          onIntakeUnavailable={props.onIntakeUnavailable}
          formId={crypto.randomUUID()}
          onPreviousTabButton={props.onPreviousTabButton}
          onNextTabButton={props.onNextTabButton}
        />
      )
    case TimelineTabs.EDUCATION_HISTORY:
      return (
        <EducationHistoryTab
          onSuccess={props.onSuccess}
          application={props.application}
          onError={props.onError}
          disabled={props.disabled}
          currentIntakeAvailable={props.currentIntakeAvailable}
          onIntakeUnavailable={props.onIntakeUnavailable}
          onPreviousTabButton={props.onPreviousTabButton}
          onNextTabButton={props.onNextTabButton}
        />
      )
    case TimelineTabs.CONTACT_INFORMATION:
      return (
        <ContactInformationTab
          onSuccess={props.onSuccess}
          application={props.application}
          onError={props.onError}
          disabled={props.disabled}
          currentIntakeAvailable={props.currentIntakeAvailable}
          onIntakeUnavailable={props.onIntakeUnavailable}
          onPreviousTabButton={props.onPreviousTabButton}
          onNextTabButton={props.onNextTabButton}
        />
      )
    default:
      return (
        <PersonalInformationTab
          onSuccess={props.onSuccess}
          application={props.application}
          onError={props.onError}
          disabled={props.disabled}
          currentIntakeAvailable={props.currentIntakeAvailable}
          onIntakeUnavailable={props.onIntakeUnavailable}
          onCreatedApplication={props.onCreatedApplication}
          isCreatingApplication={props.isCreatingApplication}
          onNextTabButton={props.onNextTabButton}
        />
      )
  }
}

const Wrapper = styled.div({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  maxWidth: '100%',
})
